/* eslint-disable import/no-duplicates */
import addDays from 'date-fns/addDays';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameMonth';
import isSameYear from 'date-fns/isSameYear';
import isValid from 'date-fns/isValid';
import de from 'date-fns/locale/de';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import frCA from 'date-fns/locale/fr-CA';
import ptBR from 'date-fns/locale/pt-BR';
import parseISO from 'date-fns/parseISO';
import parseJSON from 'date-fns/parseJSON';
import dateFnsFormat from 'date-fns-tz/format';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';

import { Locale } from './language-service';

const LOCALE_TO_DATEFNS = {
  [Locale.De]: de,
  [Locale.En]: enUS,
  [Locale.Es]: es,
  [Locale.Fr]: frCA,
  [Locale.Pt]: ptBR,
};

const DATE_MONTH_LONG = 'MMMM d, yyyy';
const TIME_12H = 'h:mmaaa';
const TIME_24H = 'H:mm';
const TIME_24H_WITHOUT_ABBREVIATION = 'H:mm:ss';
const DATE_MONTH_SHORT = 'MMM d, yyyy';
const MONTH_DAY = 'MMM d';
const YEAR = 'yyy';
const SINGLE_DAY = 'd';
const DATE_TIME = 'MMM dd yyyy, h:mma zzz';

/**
 * Returns timezone name like: 'America/New_York'
 */
function detectTimezone(): string {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Returns str formatted according to pattern
 */
function format({
  utcDate,
  pattern,
  locale,
}: {
  utcDate: Date | string | null;
  pattern: string;
  locale: Locale;
}): string {
  if (!utcDate) {
    return '';
  }

  const date = parseJSON(utcDate);

  if (!isValid(date)) {
    return '';
  }
  const timezone = detectTimezone();
  const localeDateFns = LOCALE_TO_DATEFNS[locale];
  const zonedTime = utcToZonedTime(date, timezone);

  return dateFnsFormat(zonedTime, pattern, {
    timeZone: timezone,
    locale: localeDateFns,
  });
}

/**
 * Returns str formatted like: 'March 5, 2021'
 */
export const formatDateMonthLong = (options: {
  utcDate: Date | string | null;
  locale: Locale;
}): string => format({ ...options, pattern: DATE_MONTH_LONG });
/**
 * Returns str formatted like: '2021-03-05'
 */
export const formatDateTime = (options: { utcDate: Date | string; locale: Locale }): string =>
  format({ ...options, pattern: DATE_TIME });

/**
 * Returns str formatted like: '01:00pm', or '23:00'
 */
export const formatTime = ({
  is24HourFormat,
  showAbbreviation = true,
  ...rest
}: {
  is24HourFormat: boolean;
  showAbbreviation?: boolean;
  utcDate: Date | string | null;
  locale: Locale;
}): string => {
  if (!showAbbreviation) {
    return format({
      ...rest,
      pattern: TIME_24H_WITHOUT_ABBREVIATION,
    });
  }
  return format({
    ...rest,
    pattern: is24HourFormat ? TIME_24H : TIME_12H,
  });
};

/**
 * Returns the date range according to the difference between start and end dates
 * same day -> Aug 1, 2021
 * same year and same month -> Aug 1 - 3, 2021
 * same year and different month -> Aug 1 - Sep 3, 2021
 * different year -> August 1,2021 - Sep 3 2022
 */
export const formatDateRange = (start: string, end: string, locale: Locale): string => {
  if (!start || !end) {
    return '';
  }

  const startDate = parseISO(start);
  const endDate = parseISO(end);

  const localeDateFns = LOCALE_TO_DATEFNS[locale];

  const formattedStartDate = dateFnsFormat(startDate, DATE_MONTH_SHORT, { locale: localeDateFns });

  // Same day
  if (isSameDay(startDate, endDate)) {
    return formattedStartDate;
  }

  // Same year
  if (isSameYear(startDate, endDate)) {
    const startInfo = dateFnsFormat(startDate, MONTH_DAY, { locale: localeDateFns });
    const endInfo = dateFnsFormat(endDate, MONTH_DAY, { locale: localeDateFns });
    const year = dateFnsFormat(startDate, YEAR, { locale: localeDateFns });

    if (isSameMonth(startDate, endDate)) {
      // MMM d - d, YYYY
      const endDay = dateFnsFormat(endDate, SINGLE_DAY, {
        locale: localeDateFns,
      });
      return `${startInfo} - ${endDay}, ${year}`;
    }

    // MMM d - MMM d, YYYY
    return `${startInfo} - ${endInfo}, ${year}`;
  }

  // Different year
  const formattedEndDate = dateFnsFormat(endDate, DATE_MONTH_SHORT, { locale: localeDateFns });
  return `${formattedStartDate} - ${formattedEndDate}`;
};

/**
 * Returns str formatted like: '10:00pm - 2:30am⁺¹'
 */
export const formatTimeRange = ({
  utcStartDate,
  utcEndDate,
  ...rest
}: {
  utcStartDate: Date | string | null;
  utcEndDate: Date | string | null;
  locale: Locale;
  is24HourFormat: boolean;
}): string => {
  if (!utcStartDate || !utcEndDate) {
    return '';
  }
  const startDate = parseJSON(utcStartDate);
  const endDate = parseJSON(utcEndDate);

  if (!isValid(startDate) || !isValid(endDate)) {
    return '';
  }

  const timezone = detectTimezone();
  const zonedStartDate = utcToZonedTime(startDate, timezone);
  const zonedEndDate = utcToZonedTime(endDate, timezone);
  const nextDay = addDays(zonedStartDate, 1);
  const startTime = formatTime({
    utcDate: startDate,
    ...rest,
  });
  const endTime = formatTime({
    utcDate: endDate,
    ...rest,
  });

  if (isSameDay(nextDay, zonedEndDate)) {
    const formattedEndTime = `${endTime}⁺¹`;
    return `${startTime} - ${formattedEndTime}`;
  }
  return `${startTime} - ${endTime}`;
};
